import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Home, 
  Sparkles, 
  Building2, 
  CheckCircle2, 
  Clock, 
  Star 
} from "lucide-react";

const ServicesSection = () => {
  const services = [
    {
      id: "regular",
      title: "Regular Clean",
      subtitle: "Weekly / Fortnightly / Monthly",
      icon: Home,
      image: "https://images.unsplash.com/photo-1758523670739-0d26a3ee976d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjbGVhbmVyJTIwZHVzdGluZyUyMGhvbWV8ZW58MHx8fHwxNzcwNjg4OTQ0fDA&ixlib=rb-4.1.0&q=85",
      features: [
        "Dusting and polishing surfaces",
        "Hoovering carpets",
        "Mopping hard floors",
        "Bathroom sanitisation",
        "Kitchen cleaning",
        "Emptying bins",
      ],
      size: "large",
    },
    {
      id: "detailed",
      title: "Detailed Clean",
      subtitle: "Our Signature Service",
      icon: Sparkles,
      image: "https://images.unsplash.com/photo-1665937863545-4978231e3a04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBraXRjaGVuJTIwc3BhcmtsaW5nJTIwY2xlYW58ZW58MHx8fHwxNzcwNjg4OTQ5fDA&ixlib=rb-4.1.0&q=85",
      features: [
        "All Regular services PLUS:",
        "Interior window sills & tracks",
        "Skirting board wiping",
        "Light switch & door handle sanitisation",
        "Inside microwave cleaning",
        "Kitchen cupboard fronts",
      ],
      popular: true,
      size: "medium",
    },
    {
      id: "deep",
      title: "Deep Clean / End of Tenancy",
      subtitle: "Complete Transformation",
      icon: Star,
      image: "https://images.unsplash.com/photo-1665937863545-4978231e3a04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBraXRjaGVuJTIwc3BhcmtsaW5nJTIwY2xlYW58ZW58MHx8fHwxNzcwNjg4OTQ5fDA&ixlib=rb-4.1.0&q=85",
      features: [
        "All Detailed services PLUS:",
        "Inside oven and fridge",
        "Descaling showers & taps",
        "Interior window cleaning",
        "Carpet spot treatment",
        "Wall marks removal",
      ],
      size: "medium",
    },
    {
      id: "commercial",
      title: "Commercial Services",
      subtitle: "Business Solutions",
      icon: Building2,
      image: "https://images.unsplash.com/photo-1765371513492-264506c3ad09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBtZWV0aW5nJTIwcm9vbSUyMGNsZWFuJTIwbW9kZXJufGVufDB8fHx8MTc3MDY4ODk1M3ww&ixlib=rb-4.1.0&q=85",
      features: [
        "Student accommodation turnaround",
        "Post-builders cleaning",
        "Office cleaning contracts",
        "Daily & weekly options",
        "Flexible scheduling",
        "Competitive rates",
      ],
      size: "large",
    },
  ];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 lg:py-32 bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-[#EFF6FF] rounded-full px-4 py-2 mb-6">
            <Clock className="w-4 h-4 text-[#3B82F6]" />
            <span className="text-sm font-medium text-[#3B82F6]">Professional Cleaning Solutions</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4 tracking-tight"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Our Services
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            From regular maintenance to deep cleaning transformations, we've got you covered.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isLarge = service.size === "large";
            
            return (
              <Card
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className={`relative overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 card-hover bg-white group ${
                  isLarge ? "lg:col-span-2 lg:row-span-1" : ""
                } ${index === 0 ? "lg:row-span-1" : ""}`}
              >
                {service.popular && (
                  <div className="absolute top-4 right-4 z-10">
                    <span className="inline-flex items-center gap-1 bg-[#3B82F6] text-white text-xs font-semibold px-3 py-1 rounded-full pulse-badge">
                      <Star className="w-3 h-3" fill="currentColor" />
                      Popular
                    </span>
                  </div>
                )}
                
                <div className={`grid ${isLarge ? "md:grid-cols-2" : "grid-cols-1"} h-full`}>
                  {/* Image */}
                  <div className={`relative overflow-hidden ${isLarge ? "h-64 md:h-full" : "h-48"}`}>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-10 h-10 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#3B82F6]" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <CardHeader className="p-0 mb-4">
                      <p className="text-sm text-[#3B82F6] font-medium mb-1">{service.subtitle}</p>
                      <CardTitle 
                        className="text-xl font-bold text-[#0F172A]"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ul className="space-y-2">
                        {service.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-sm text-[#64748B]">
                            <CheckCircle2 className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
